import { useNavigation } from '@react-navigation/native'
import * as WebBrowser from 'expo-web-browser'
import { Box, Center, Image, Row, ScrollView, Text } from 'native-base'
import { ActivityIndicator, Platform, useWindowDimensions } from 'react-native'
import Animated, { useAnimatedStyle, SharedValue } from 'react-native-reanimated'
import RenderHtml from 'react-native-render-html'

import {
  BottomComponentWithButton,
  BoxWithShadow,
  Icon,
  LoadingState,
  OfferCodeBox,
  PointsBox,
  ScrollViewWithAnimatedHeader,
  Spacer,
  WebBackButtonBar,
} from '~components'
import {
  detailsImgSizeHorizontalLayout,
  detailsImgSizeVerticalLayout,
  webNavBarHeight,
} from '~constants'
import {
  useCallback,
  useDeviceInfo,
  useGetFilteredOffers,
  useMemo,
  useRef,
  useSafeAreaInsets,
  useState,
  useToast,
  useTranslation,
} from '~hooks'
import { Images } from '~images'
import { useRedeemOffer, useGetOfferDetails, useGetUser } from '~query-hooks'
import { formatDate, getOfferStatus } from '~utils'

const AnimatedImage = Animated.createAnimatedComponent(Image)

export const OfferDetailsScreen = ({ route: { params } }: OfferDetailsScreenProps): JSX.Element => {
  const { offerId } = params

  const { goBack } = useNavigation()
  const { bottom, top } = useSafeAreaInsets()
  const { showToast } = useToast()
  const { t } = useTranslation()
  const { width } = useWindowDimensions()
  const { isWeb, shouldApplyDesktopStyles } = useDeviceInfo()

  const { offerDetails, isLoadingOfferDetails, refetchOfferDetails } = useGetOfferDetails({
    offerId,
  })

  const { refetchOffers } = useGetFilteredOffers({ enabled: false })

  const { refetchUser } = useGetUser()
  const { redeemOffer, isLoadingRedeemOffer } = useRedeemOffer()
  const IMAGE_HEIGHT = shouldApplyDesktopStyles
    ? detailsImgSizeHorizontalLayout
    : detailsImgSizeVerticalLayout
  const [isLoadingImage, setIsLoadingImage] = useState(true)

  const setImageLoaded = useCallback(() => {
    if (isLoadingImage) {
      setIsLoadingImage(false)
    }
  }, [isLoadingImage])

  const scrollViewRef = useRef<{ scrollOffsetValue: SharedValue<number> }>()
  const sharedValue = scrollViewRef.current?.scrollOffsetValue

  const {
    buyStatus,
    description,
    discountCode,
    discountUsed,
    endDate,
    image,
    merchant,
    merchantUrl,
    points,
    title,
    offerLeft,
  } = offerDetails || {}
  const { logo: merchantLogo, name: merchantName } = merchant || {}
  const isRedeemed = !!buyStatus
  const isUsed = !!discountUsed

  const offerStatus = useMemo(() => {
    return getOfferStatus({
      buyStatus,
      endDate,
      offerLeft,
      discountUsed,
    })
  }, [buyStatus, discountUsed, endDate, offerLeft])

  const redeemHandler = useCallback(async () => {
    try {
      const response = await redeemOffer({ offerId })

      const { success } = response

      if (success) {
        showToast({ description: t('toast_messages.coupon_redeemed'), variant: 'success' })

        await Promise.all([refetchOfferDetails(), refetchUser(), refetchOffers()])
      }
    } catch (e) {
      const message = (e as Error).message
      showToast({ description: message, variant: 'danger' })
    }
  }, [redeemOffer, offerId, showToast, t, refetchOfferDetails, refetchUser, refetchOffers])

  const navigateToMerchantStore = useCallback(async () => {
    try {
      if (merchantUrl && (merchantUrl.includes('http://') || merchantUrl.includes('https://'))) {
        if (Platform.OS === 'web') {
          if (typeof window !== 'undefined') {
            window.open(merchantUrl, '_blank', 'noopener,noreferrer')
          }
        } else {
          await WebBrowser.openBrowserAsync(merchantUrl)
        }
        await refetchOfferDetails()
      }
    } catch (e) {
      console.log(e)
    }
  }, [merchantUrl, refetchOfferDetails])

  const imageStyle = useAnimatedStyle(() => {
    const value = sharedValue?.value && sharedValue.value > 0 ? 0 : sharedValue?.value || 0
    return {
      height: IMAGE_HEIGHT - value,
      transform: [{ translateY: value }],
    }
  })

  const renderExtraContent = useMemo(() => {
    if (!offerStatus) return null
    return (
      <>
        <Spacer y="10" flex={1} />
        <BoxWithShadow borderRadius="lg" mx="4" py="2.5" shadowSize="l">
          <Center>
            <Row alignItems="center">
              {!isRedeemed && (
                <>
                  <Icon color="black" name="timer-line" size={15} />
                  <Spacer x="2.5" />
                </>
              )}
              <Text fontWeight="500" color={isUsed ? 'offMain' : 'black'} textAlign="center">
                {offerStatus}
              </Text>
            </Row>
          </Center>
        </BoxWithShadow>
      </>
    )
  }, [offerStatus, isRedeemed, isUsed])

  const renderOfferImage = useMemo(
    () => (
      <Box h={IMAGE_HEIGHT} w="full">
        <AnimatedImage
          alt="Product image"
          fallbackSource={Images.OFFER_IMAGE_PLACEHOLDER}
          loadingIndicatorSource={{ uri: image }}
          onError={setImageLoaded}
          onLoadEnd={setImageLoaded}
          resizeMode="cover"
          source={{ uri: image }}
          style={imageStyle}
          w="full"
        />
        {isLoadingImage && (
          <Center h="full" position="absolute" pt={`${top}px`} w="full" zIndex={10}>
            <ActivityIndicator size="large" />
          </Center>
        )}
      </Box>
    ),
    [image, IMAGE_HEIGHT, imageStyle, isLoadingImage, setImageLoaded, top]
  )
  const renderOfferContent = useMemo(
    () => (
      <>
        <Box alignItems="center" bg="white" pb="8" px="4">
          <PointsBox cornersType="bottom" isRedeemed={isRedeemed} points={points || '-'} />
          <Spacer y="4" />
          {isRedeemed && (
            <Box pb="6" pt="2" px="10" {...(shouldApplyDesktopStyles && { w: '95%', px: 0 })}>
              <OfferCodeBox code={discountCode || '-'} isUsed={isUsed} />
              <Text color="offMain" fontSize="sm" mt="3" textAlign="center">
                {t('offer_details_screen.copy_coupon_instruction')}
              </Text>
            </Box>
          )}
          <Text bold fontSize="2xl" textAlign="center">
            {title || '-'}
          </Text>
          <Spacer y="6" />
          <Text fontSize="md" fontWeight="500" textAlign="center">
            {merchantName || '-'}
          </Text>
          <Spacer y="4" />
          <Image
            alt="Brand logo"
            source={merchantLogo ? { uri: merchantLogo } : Images.ADIDAS_LOGO}
          />
          <Box pt="6" px="8">
            <RenderHtml
              contentWidth={width}
              source={{
                html: description || '',
              }}
            />
          </Box>
          {!isRedeemed && endDate && (
            <Text color="offMain" fontWeight="500" mt="4">
              {t('offer_details_screen.available_till')} {formatDate(endDate)}
            </Text>
          )}
        </Box>
        {renderExtraContent}
      </>
    ),
    [
      description,
      discountCode,
      endDate,
      isRedeemed,
      isUsed,
      merchantLogo,
      merchantName,
      points,
      renderExtraContent,
      shouldApplyDesktopStyles,
      t,
      title,
      width,
    ]
  )

  const BottomComponent = useMemo(() => {
    if ((!isRedeemed || !isUsed) && !isLoadingOfferDetails) {
      return (
        <BottomComponentWithButton
          buttonLabel={t(
            `offer_details_screen.${isRedeemed ? 'go_to_merchant_store' : 'redeem_offer'}`
          )}
          buttonProps={{
            isLoading: isLoadingRedeemOffer,
            onPress: !isRedeemed && !isUsed ? redeemHandler : navigateToMerchantStore,
          }}
          componentProps={{ pb: `${bottom}px` }}
          withShadow={!isWeb}
        />
      )
    }
    return null
  }, [
    bottom,
    isWeb,
    isLoadingOfferDetails,
    isLoadingRedeemOffer,
    isRedeemed,
    isUsed,
    navigateToMerchantStore,
    redeemHandler,
    t,
  ])
  const renderContentForMobile = useMemo(() => {
    if (isLoadingOfferDetails) return <LoadingState />
    return (
      <>
        {renderOfferImage}
        {renderOfferContent}
      </>
    )
  }, [isLoadingOfferDetails, renderOfferContent, renderOfferImage])

  const renderContentForDesktop = useMemo(
    () => (
      <>
        <WebBackButtonBar />
        {isLoadingOfferDetails ? (
          <LoadingState />
        ) : (
          <Box pb={webNavBarHeight} my="auto">
            <Row h={IMAGE_HEIGHT} w="full" rounded="lg" overflow="hidden">
              <Box w="65%">{renderOfferImage}</Box>
              <Box w="35%">
                <ScrollView>{renderOfferContent}</ScrollView>
                {BottomComponent}
              </Box>
            </Row>
          </Box>
        )}
      </>
    ),
    [BottomComponent, IMAGE_HEIGHT, isLoadingOfferDetails, renderOfferContent, renderOfferImage]
  )

  if (shouldApplyDesktopStyles) return renderContentForDesktop
  return (
    <ScrollViewWithAnimatedHeader
      BottomComponent={BottomComponent}
      headerTitle={title || ''}
      onPress={goBack}
      ref={scrollViewRef}
    >
      {renderContentForMobile}
    </ScrollViewWithAnimatedHeader>
  )
}
